import React, { useEffect, useMemo, useState } from "react";
import { UserPlus, Copy, Trash2, Check, Settings2, Pencil, Users as UsersIcon, Building2, Upload } from "lucide-react";
import { api, apiErrorMessage } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import AuthedAvatar from "../../components/app/AuthedAvatar";
import MemberProfileDrawer from "../../components/app/MemberProfileDrawer";
import CatalogModal from "../../components/app/CatalogModal";
import ImportWizard from "../../components/app/ImportWizard";
import { Checkbox } from "../../components/ui/checkbox";
import { BulkActionsBar } from "../../components/app/BulkActionsBar";

export default function Members() {
  const { activeWorkspace, user } = useAuth();
  const { toast } = useToast();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [roleTags, setRoleTags] = useState([]);
  const [deptTags, setDeptTags] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [copied, setCopied] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drawerFor, setDrawerFor] = useState(null);
  const [catalog, setCatalog] = useState(null); // "roles" | "departments" | null
  const [importFor, setImportFor] = useState(null); // "team" | "roles" | "departments" | null
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    if (!activeWorkspace) return;
    const [m, i, r, d] = await Promise.all([
      api.members(activeWorkspace.id),
      api.invites(activeWorkspace.id).catch(() => []),
      api.roleTags(false).catch(() => []),
      api.deptTags(false).catch(() => []),
    ]);
    setMembers(m); setInvites(i);
    setRoleTags(r); setDeptTags(d);
    setSelected(new Set());
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspace?.id]);

  const myRole = members.find((m) => m.user_id === user?.id)?.role;
  const canInvite = myRole === "owner" || myRole === "admin";
  const isRemovable = (m) => m.role !== "owner" && m.user_id !== user?.id;
  const removableMembers = useMemo(() => members.filter(isRemovable), [members, user?.id]);

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;
    setBusy(true);
    try {
      const inv = await api.createInvite(activeWorkspace.id, { email: inviteEmail, role: inviteRole });
      setInviteEmail("");
      toast(inv.email_sent
        ? { title: "Invite sent", description: `We emailed ${inv.email} a link to join.` }
        : { title: "Invite created", description: "Couldn't send the email — copy the invite link below to share instead." });
      await load();
    } catch (ex) {
      toast({ title: "Couldn't create invite", description: apiErrorMessage(ex, "Please try again.") });
    } finally { setBusy(false); }
  };

  const copyLink = async (inv) => {
    try {
      await navigator.clipboard.writeText(inv.invite_url);
      setCopied(inv.id);
      setTimeout(() => setCopied(null), 1500);
    } catch {}
  };

  const revoke = async (inv) => {
    await api.revokeInvite(activeWorkspace.id, inv.id);
    await load();
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.name} from ${activeWorkspace.name}?`)) return;
    await api.removeMember(activeWorkspace.id, m.user_id);
    await load();
  };

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const clearSelection = () => setSelected(new Set());
  const toggleSelectAll = () => setSelected((prev) =>
    prev.size === removableMembers.length ? new Set() : new Set(removableMembers.map((m) => m.user_id))
  );

  const bulkRemove = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!window.confirm(`Remove ${ids.length} selected member${ids.length === 1 ? "" : "s"} from ${activeWorkspace.name}?`)) return;
    const results = await Promise.allSettled(ids.map((id) => api.removeMember(activeWorkspace.id, id)));
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - ok;
    if (ok > 0) toast({ title: `${ok} member${ok === 1 ? "" : "s"} removed` });
    if (failed > 0) toast({ title: `${failed} member(s) failed to remove`, description: "Please retry individually." });
    clearSelection();
    await load();
  };

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-sm text-[#6a736f]">Team</div>
          <h1 className="font-display text-4xl mt-1">{activeWorkspace?.name}</h1>
        </div>
        {canInvite && (
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setImportFor("team")}
              data-testid="import-team-btn"
              className="btn-outline-forest inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium"
            >
              <Upload size={14} /> Import CSV
            </button>
            <button
              onClick={() => setCatalog("roles")}
              data-testid="manage-roles-btn"
              className="btn-outline-forest inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium"
            >
              <UsersIcon size={14} /> Manage roles
            </button>
            <button
              onClick={() => setCatalog("departments")}
              data-testid="manage-depts-btn"
              className="btn-outline-forest inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium"
            >
              <Building2 size={14} /> Manage departments
            </button>
            <BulkActionsBar
              count={selected.size}
              onClear={clearSelection}
              testidPrefix="members"
              actions={[
                { key: "remove", label: `Remove selected${selected.size ? ` (${selected.size})` : ""}`, icon: Trash2, danger: true, onClick: bulkRemove },
              ]}
            />
          </div>
        )}
      </div>

      {canInvite && (
        <form onSubmit={sendInvite} className="mt-6 bg-white rounded-xl border border-cream p-5 flex flex-col md:flex-row gap-3 items-stretch md:items-end">
          <label className="flex-1 text-sm">
            <span className="font-medium">Invite by email</span>
            <input type="email" required value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="colleague@company.com"
              className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm" />
          </label>
          <label className="text-sm">
            <span className="font-medium">Role</span>
            <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}
              className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm">
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </label>
          <button disabled={busy} className="btn-coral px-5 py-2 rounded-md text-sm font-medium flex items-center gap-2">
            <UserPlus size={16} /> {busy ? "Sending…" : "Send invite"}
          </button>
        </form>
      )}

      {invites.length > 0 && (
        <div className="mt-6">
          <div className="font-semibold mb-2">Pending invites</div>
          <div className="bg-white rounded-xl border border-cream divide-y divide-[#EAF3EE]">
            {invites.map((inv) => (
              <div key={inv.id} className="px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium">{inv.email}</div>
                  <div className="text-xs text-[#6a736f]">Role: {inv.role} · <span className="break-all">{inv.invite_url}</span></div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => copyLink(inv)} className="btn-outline-forest px-3 py-1.5 rounded-md text-xs flex items-center gap-1">
                    {copied === inv.id ? <Check size={14} /> : <Copy size={14} />}
                    {copied === inv.id ? "Copied" : "Copy link"}
                  </button>
                  <button onClick={() => revoke(inv)} className="text-[#6a736f] hover:text-coral"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-[#6a736f]">We email each invite automatically — copying the link above is just a handy backup if they don't see it.</p>
        </div>
      )}

      <div className="mt-8">
        <div className="font-semibold mb-2">Members</div>
        <div className="bg-white rounded-xl border border-cream overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-cream-2/50 text-xs text-[#6a736f]">
              <tr className="text-left">
                {canInvite && (
                  <th className="px-4 py-3 w-8">
                    <Checkbox checked={removableMembers.length > 0 && selected.size === removableMembers.length}
                              onCheckedChange={toggleSelectAll}
                              data-testid="select-all-members" />
                  </th>
                )}
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Role tags</th>
                <th className="px-4 py-3">Departments</th>
                <th className="px-4 py-3">Access</th>
                <th className="px-4 py-3">Capacity</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EAF3EE]">
              {members.map((m) => {
                const rTags = (m.roles || []).map((id) => roleTags.find((t) => t.id === id)?.name).filter(Boolean);
                const dTags = (m.departments || []).map((id) => deptTags.find((t) => t.id === id)?.name).filter(Boolean);
                return (
                  <tr key={m.user_id} data-testid={`member-row-${m.user_id}`}>
                    {canInvite && (
                      <td className="px-4 py-3">
                        {isRemovable(m) && (
                          <Checkbox checked={selected.has(m.user_id)} onCheckedChange={() => toggleSelect(m.user_id)}
                                    data-testid={`select-member-${m.user_id}`} />
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => canInvite && setDrawerFor(m)}
                        data-testid={`open-profile-${m.user_id}`}
                        className={`flex items-center gap-3 text-left ${canInvite ? "hover:text-coral" : "cursor-default"}`}
                      >
                        <AuthedAvatar userId={m.user_id} name={m.name} size={32} />
                        <div className="min-w-0">
                          <div className="font-medium">{m.name}</div>
                          <div className="text-[11px] text-[#6a736f]">{m.email}{m.employee_id ? ` · ${m.employee_id}` : ""}</div>
                        </div>
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      {rTags.length === 0 ? <span className="text-[#6a736f]">—</span> :
                        <div className="flex flex-wrap gap-1">
                          {rTags.map((n) => <span key={n} className="text-[10px] bg-coral/15 text-coral rounded px-1.5 py-0.5">{n}</span>)}
                        </div>}
                    </td>
                    <td className="px-4 py-3">
                      {dTags.length === 0 ? <span className="text-[#6a736f]">—</span> :
                        <div className="flex flex-wrap gap-1">
                          {dTags.map((n) => <span key={n} className="text-[10px] bg-forest/10 text-forest rounded px-1.5 py-0.5">{n}</span>)}
                        </div>}
                    </td>
                    <td className="px-4 py-3 capitalize">{m.role}</td>
                    <td className="px-4 py-3 tabular-nums">{(m.capacity_hours ?? 40).toFixed(1)}h/wk</td>
                    <td className="px-4 py-3 text-right space-x-3">
                      {canInvite && (
                        <button onClick={() => setDrawerFor(m)}
                                data-testid={`edit-profile-${m.user_id}`}
                                className="text-forest hover:text-coral inline-flex items-center gap-1 text-xs">
                          <Pencil size={12} /> Edit
                        </button>
                      )}
                      {canInvite && m.role !== "owner" && m.user_id !== user?.id && (
                        <button onClick={() => removeMember(m)}
                                data-testid={`remove-member-${m.user_id}`}
                                className="text-[#6a736f] hover:text-coral">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <MemberProfileDrawer
        open={!!drawerFor}
        member={drawerFor}
        workspaceId={activeWorkspace?.id}
        canEdit={canInvite}
        currentUserId={user?.id}
        onClose={() => setDrawerFor(null)}
        onSaved={(updated) => {
          setMembers((prev) => prev.map((mm) => mm.user_id === updated.user_id ? updated : mm));
          setDrawerFor(updated);
        }}
        toast={toast}
      />

      {catalog === "roles" && (
        <CatalogModal
          title="Roles"
          hint="Job titles or seniorities — Designer, Senior, PM, Tech Lead."
          listFn={api.roleTags}
          createFn={api.createRoleTag}
          updateFn={api.updateRoleTag}
          deleteFn={api.deleteRoleTag}
          testid="roles-catalog"
          onClose={async () => { setCatalog(null); await load(); }}
        />
      )}
      {catalog === "departments" && (
        <CatalogModal
          title="Departments"
          hint="Cost centres or teams — Advisory, Design, Engineering, Operations."
          listFn={api.deptTags}
          createFn={api.createDeptTag}
          updateFn={api.updateDeptTag}
          deleteFn={api.deleteDeptTag}
          testid="depts-catalog"
          onClose={async () => { setCatalog(null); await load(); }}
        />
      )}

      <ImportWizard
        entity={importFor}
        open={!!importFor}
        onClose={() => setImportFor(null)}
        onImported={() => load()}
        toast={toast}
      />
    </div>
  );
}
